import { AccountError, type CommerceSource, type RegisterRequest } from '@akademi/site-builder-runtime';
import { NEW_PASSWORD_MIN_LENGTH, safeNext } from './accountShared';

/**
 * Signing up with a password, as online shops do: the account opens at once and the shopper is
 * signed in straight away, and the shop emails a welcome. A shop whose set-up still asks new
 * accounts to confirm their email emails a link and a code instead, as before.
 *
 * <p>Akademi's `CommerceSource.register` is typed for the older sign-up, name and email only and
 * resolving with nothing. These types are what this shop's host adds to it, until Akademi's own
 * contract says so; {@link signUp} is the one place that bridges the two.
 */

/** A new account, with the password the shopper chose, typed twice. */
export interface NewAccountRequest extends RegisterRequest {
  password: string;
  confirmPassword: string;
  /**
   * Apply for a trade account (`TRADE`) or to join the shop's installers (`INSTALLER`) in the same
   * step as making the account (contract §7). Absent: an ordinary shopper's sign-up.
   */
  accountType?: 'TRADE' | 'INSTALLER';
  /** The application the trade or installer form sends, without files; the address is the account's. */
  application?: object;
}

/** What a sign-up came to. */
export interface SignUpOutcome {
  /** True: a link and a code went to the address, and nobody is signed in until they come back. */
  activationRequired: boolean;
  /** True: the account is open and this browser is signed in to it. */
  signedIn: boolean;
}

/** Why a new password was refused, as the account error it reads as. */
export type NewPasswordProblem = 'WEAK_PASSWORD' | 'PASSWORDS_MISMATCH';

/**
 * The base's rule for a new password: at least {@link NEW_PASSWORD_MIN_LENGTH} characters, with a
 * digit, an upper-case letter and a lower-case letter. Checked here only so the shopper hears it
 * before the round trip; the base checks it again and has the last word.
 */
export function isStrongPassword(password: string): boolean {
  return (
    password.length >= NEW_PASSWORD_MIN_LENGTH &&
    /\p{Nd}/u.test(password) &&
    /\p{Lu}/u.test(password) &&
    /\p{Ll}/u.test(password)
  );
}

/** What is wrong with a new password and its confirmation, or null. A weak password is said first. */
export function newPasswordProblem(password: string, confirmation: string): NewPasswordProblem | null {
  if (!isStrongPassword(password)) return 'WEAK_PASSWORD';
  if (password !== confirmation) return 'PASSWORDS_MISMATCH';
  return null;
}

/**
 * A host's answer to `register`, read safely.
 *
 * <p>Only an explicit `activationRequired: false` opens the account at once. Anything else — the
 * older host that answers nothing, or an answer that does not say — is the emailed code, which is
 * what such a host did.
 */
export function signUpOutcome(answer: unknown): SignUpOutcome {
  const fields = answer && typeof answer === 'object' ? (answer as Record<string, unknown>) : {};
  const activationRequired = fields.activationRequired !== false;
  return { activationRequired, signedIn: !activationRequired && fields.signedIn === true };
}

/** Creates the account through the host, with its password; see the note at the top. */
export async function signUp(commerce: CommerceSource, request: NewAccountRequest): Promise<SignUpOutcome> {
  const register = commerce.register as ((request: NewAccountRequest) => Promise<unknown>) | undefined;
  if (!register) throw new AccountError('UNAVAILABLE', 0);
  return signUpOutcome(await register.call(commerce, request));
}

/**
 * Where the shopper goes once the account is made, or null to stay and say an email is on its way.
 *
 * <p>Signed in: `?next=` when that is a path on this site, else `successHref` — as after signing
 * in. Open but not signed in (the sign-in that follows a sign-up did not work): the sign-in page,
 * carrying `?next=` on, so the password just chosen takes them where they were going.
 */
export function afterSignUpHref(
  outcome: SignUpOutcome,
  pages: { next: string | null | undefined; successHref: string; signInHref: string },
): string | null {
  if (outcome.activationRequired) return null;
  const next = safeNext(pages.next);
  if (outcome.signedIn) return next ?? pages.successHref;
  if (!next) return pages.signInHref;
  const joiner = pages.signInHref.includes('?') ? '&' : '?';
  return `${pages.signInHref}${joiner}next=${encodeURIComponent(next)}`;
}
